import React, { useEffect, useState, useRef, useCallback } from "react";
import { adminGetShortTest, userGetShorts } from "../../apiservice/userService";
import { IShort, IShortData } from "../../apiservice/userService.type";
import ShortCard from "./ShortCard";

const ITEMS_PER_PAGE = 20; // Define items per page for pagination

const ShortsCarousel: React.FC = () => {
  const [shorts, setShorts] = useState<IShortData[]>([]);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true); // For the very first load
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false); // For subsequent page loads
  const [error, setError] = useState<string | null>(null);
  const [activeShortId, setActiveShortId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  // "90vh" is used instead of "100vh" because of the menu

  // Refs for IntersectionObserver
  const observer = useRef<IntersectionObserver | null>(null);
  const shortRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Ensure shortRefs array is populated correctly when shorts state updates
  useEffect(() => {
    shortRefs.current = shortRefs.current.slice(0, shorts.length);
  }, [shorts.length]);

  const fetchShortsData = useCallback(
    async (pageToFetch: number) => {
      if (!hasNextPage && pageToFetch > 1) {
        console.log("No more pages to fetch.");
        return;
      }

      if (pageToFetch === 1) {
        setLoadingInitial(true);
      } else {
        setIsFetchingMore(true);
      }
      setError(null);

      try {
        const response: IShort = await userGetShorts({
          pageSize: ITEMS_PER_PAGE,
          page: pageToFetch,
        });
        if (response.status === 200) {
          setShorts((prevShorts) =>
            pageToFetch === 1
              ? response.data
              : [...prevShorts, ...response.data]
          );

          const totalPage = Number(
            (response?.meta?.total || 0) / ITEMS_PER_PAGE
          );

          const hasNextpage_ = pageToFetch < totalPage;
          setTotalPage(totalPage);

          // console.log(totalPage);
          // console.log(hasNextpage_);

          if (hasNextPage) {
            setCurrentPage(pageToFetch);
          }

          setHasNextPage(hasNextpage_);
          if (pageToFetch === 1 && response.data.length > 0) {
            // setActiveShortId(response.data[0].id); // Let IntersectionObserver handle this
          }
        } else {
          setError(response.message || "Failed to fetch shorts");
        }
      } catch (err) {
        setError("An error occurred while fetching shorts");
        console.error(err);
      } finally {
        if (pageToFetch === 1) {
          setLoadingInitial(false);
        } else {
          setIsFetchingMore(false);
        }
      }
    },
    [hasNextPage]
  );

  useEffect(() => {
    fetchShortsData(1); // Initial fetch
  }, [fetchShortsData]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          const shortIdString = entry.target.getAttribute("data-short-id");
          if (shortIdString) {
            const shortId = parseInt(shortIdString, 10);
            setActiveShortId(shortId);
          }
        }
      });
    },
    []
  );

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.55,
    };

    observer.current = new IntersectionObserver(handleIntersection, options);
    const currentObserver = observer.current;

    shortRefs.current.forEach((ref) => {
      if (ref) currentObserver.observe(ref);
    });

    return () => {
      currentObserver.disconnect();
      shortRefs.current.forEach((ref) => {
        if (ref) currentObserver.unobserve(ref);
      });
    };
  }, [shorts, handleIntersection]);

  const handleVideoEnded = useCallback(
    (endedShortId: number) => {
      const currentIndex = shorts.findIndex((s) => s.id === endedShortId);

      if (currentIndex === -1) return;

      if (currentIndex < shorts.length - 1) {
        // Not the last video in the current list
        const nextShortElement = shortRefs.current[currentIndex + 1];
        nextShortElement?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        // IntersectionObserver will handle setting activeShortId and playing the video
      } else if (hasNextPage && !isFetchingMore) {
        // It is the last video, and there's a next page, and not already fetching
        setCurrentPage((prevPage) => {
          const nextPage = prevPage + 1;
          fetchShortsData(nextPage);
          return nextPage;
        });
      } else {
        // Last video of all available shorts, or already fetching. Optionally, loop to first video or do nothing.
        console.log("End of all shorts or already fetching new page.");
        // Example: scroll to the first video to loop
        // shortRefs.current[0]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [shorts, fetchShortsData, hasNextPage, isFetchingMore]
  );

  if (loadingInitial && shorts.length === 0) {
    // Show main loader only if no shorts are displayed yet
    return (
      <div
        style={{
          width: "100vw",
          height: "90vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        Loading Shorts...
      </div>
    );
  }

  if (error && shorts.length === 0) {
    // Show main error only if no shorts are displayed
    return (
      <div
        style={{
          width: "100vw",
          height: "90vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#111",
          color: "red",
          padding: "20px",
          textAlign: "center",
        }}
      >
        Error: {error}
      </div>
    );
  }

  if (shorts.length === 0 && !loadingInitial && !error) {
    // No shorts available, not loading, no error
    return (
      <div
        style={{
          width: "100vw",
          height: "90vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        No shorts available.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100vw",
        height: "90vh",
        overflowY: "auto",
        scrollSnapType: "y mandatory",
        backgroundColor: "red",
      }}
    >
      <style>
        {`
          body { overflow: hidden; }
          .shorts-container::-webkit-scrollbar { display: none; }
          .shorts-container { -ms-overflow-style: none; scrollbar-width: none; }
        `}
      </style>
      <div
        className="shorts-container"
        style={{ width: "100vw", height: "90%" }}
      >
        {shorts.map((short, index) => (
          <div
            key={short.id + "-" + index} // Ensure key is unique even if shorts have same ID temporarily during loading
            ref={(el) => (shortRefs.current[index] = el)}
            data-short-id={short.id}
            style={{
              height: "90vh",
              width: "100vw",
              scrollSnapAlign: "start",
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <ShortCard
              short={short}
              isActive={short.id === activeShortId}
              onVideoEnded={() => handleVideoEnded(short.id)}
            />
          </div>
        ))}
        {isFetchingMore && (
          <div
            style={{
              height: "90vh",
              width: "100vw",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#fff",
              scrollSnapAlign: "start",
              position: "absolute",
              bottom: 0,
              left: 0,
              background: "rgba(0,0,0,0.5)",
            }}
          >
            Loading more shorts...
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortsCarousel;
